import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [isStatsPaused, setIsStatsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hasAnimatedStats, setHasAnimatedStats] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const sectionRef = useRef(null);
  const statsContainerRef = useRef(null);
  
  // Mouse position for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring for mouse movement
  const springConfig = { damping: 25, stiffness: 150 };
  const mouseXSpring = useSpring(mouseX, springConfig);
  const mouseYSpring = useSpring(mouseY, springConfig);
  
  // Transform for content parallax based on mouse
  const contentX = useTransform(mouseXSpring, (v) => v * -0.3);
  const contentY = useTransform(mouseYSpring, (v) => v * -0.3);
  
  // Scroll-based parallax
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 100]);
  
  // Houston skyline images for carousel
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/akpbx2hy_5889f800-06bf-4a62-a002-88b9ee2ccb34.png',
    'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/n1a8dpct_4194d8b2-3462-4e90-9ef9-c2379199029c.png',
    'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/ljhc83kc_7568ad1b-d8ac-45b1-ae1c-e6de4386ce95.png',
    'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/ds9xgvof_493cf26b-d4a7-4da0-bc43-847cf7f0c91d.png',
    'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/vzehlcif_77954683-06cb-45f4-b8e8-35d10a4495f6.png'
  ];

  // 6 Stats data - Story flow order
  const heroStats = [
    { value: 18, suffix: '+', label: 'Years Real Estate Experience' },
    { value: 100, suffix: '+', label: 'Deals Completed' },
    { value: '15–30', suffix: '%', label: 'Historical Annual Returns', isRange: true },
    { value: 70, suffix: '+', label: 'Successful Exits' },
    { value: 0, suffix: '', label: 'Investor Losses' },
    { value: '20–30', suffix: '%', label: 'Below Market Acquisitions', isRange: true }
  ];

  // Check for reduced motion preference and screen size
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Mouse move handler for parallax effect (desktop only)
  useEffect(() => {
    if (isMobile || prefersReducedMotion) return;
    
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      
      // Normalize to -1 to 1
      const x = (clientX / innerWidth - 0.5) * 2;
      const y = (clientY / innerHeight - 0.5) * 2;
      
      mouseX.set(x * 10); // Max 10px movement
      mouseY.set(y * 10);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile, prefersReducedMotion, mouseX, mouseY]);

  // Auto-advance background image carousel every 7 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Auto-advance stats carousel every 3.5 seconds (unless paused)
  useEffect(() => {
    if (isStatsPaused) return;
    
    const interval = setInterval(() => {
      setCurrentStatIndex((prev) => (prev + 1) % heroStats.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isStatsPaused, heroStats.length]);

  // Intersection observer for stats animation
  useEffect(() => {
    if (!statsContainerRef.current || hasAnimatedStats) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedStats) {
          setHasAnimatedStats(true);
        }
      },
      { threshold: 0.5 }
    );
    
    observer.observe(statsContainerRef.current);
    return () => observer.disconnect();
  }, [hasAnimatedStats]);

  // Get visible stats based on screen size
  const getVisibleStats = () => {
    const count = isMobile ? 2 : 4;
    const stats = [];
    for (let i = 0; i < count; i++) {
      const index = (currentStatIndex + i) % heroStats.length;
      stats.push({ ...heroStats[index], originalIndex: index });
    }
    return stats;
  };

  // Animated counter component
  const AnimatedCounter = ({ value, prefix = '', suffix = '', isRange = false, shouldAnimate }) => {
    const [displayValue, setDisplayValue] = useState(isRange ? value : 0);
    
    useEffect(() => {
      if (!shouldAnimate || prefersReducedMotion || isRange) {
        setDisplayValue(value);
        return;
      }
      
      const duration = 1000;
      const steps = 60;
      const stepDuration = duration / steps;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setDisplayValue(value);
          clearInterval(timer);
        } else {
          setDisplayValue(Math.floor(current));
        }
      }, stepDuration);
      
      return () => clearInterval(timer);
    }, [value, shouldAnimate, isRange]);
    
    return <>{prefix}{displayValue}{suffix}</>;
  };

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setCurrentStatIndex((prev) => (prev + 1) % heroStats.length);
    } else if (isRightSwipe) {
      setCurrentStatIndex((prev) => (prev - 1 + heroStats.length) % heroStats.length);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2
      }
    }
  };

  const headlineVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const statsContainerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: 0.6
      }
    }
  };

  // Ken Burns effect - slow zoom over 15 seconds
  const imageVariants = {
    enter: {
      opacity: 0,
      scale: 1
    },
    center: {
      opacity: 1,
      scale: prefersReducedMotion ? 1 : 1.05,
      transition: {
        opacity: { duration: 1.5, ease: "easeOut" },
        scale: { duration: 15, ease: "linear" }
      }
    },
    exit: {
      opacity: 0,
      transition: {
        opacity: { duration: 1.5, ease: "easeIn" }
      }
    }
  };

  // Stat item animation variants - smooth slide
  const statItemVariants = {
    enter: {
      x: 40,
      opacity: 0
    },
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: {
      x: -40,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen overflow-hidden">
      {/* Background Image with Parallax and Ken Burns */}
      <motion.div 
        className="absolute inset-0"
        style={!isMobile && !prefersReducedMotion ? { 
          y: backgroundY,
          x: mouseXSpring,
        } : {}}
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={currentImageIndex}
            className="absolute inset-[-20px] bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${heroImages[currentImageIndex]}')`
            }}
            variants={imageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          />
        </AnimatePresence>
      </motion.div>
      
      {/* Multi-layer gradient overlay for premium depth */}
      {/* Layer 1: Strong left-to-right gradient for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/30 md:to-transparent" />
      
      {/* Layer 2: Top vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/40 via-transparent to-transparent" />
      
      {/* Layer 3: Bottom vignette for stats area */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/20 to-transparent" />
      
      {/* Layer 4: Radial vignette for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,22,40,0.3)_100%)]" />
      
      {/* Subtle animated light sweep (respects reduced motion) */}
      {!prefersReducedMotion && (
        <motion.div 
          className="absolute inset-0 opacity-[0.015] pointer-events-none"
          style={{
            background: 'linear-gradient(105deg, transparent 40%, rgba(212,175,55,0.5) 50%, transparent 60%)',
            backgroundSize: '200% 100%'
          }}
          animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
        />
      )}

      {/* Main Content */}
      <div className="relative z-10 max-w-[1500px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 xl:px-20 pt-24 sm:pt-28 lg:pt-36 pb-6 sm:pb-8 min-h-[90vh] sm:min-h-screen flex flex-col">
        <motion.div 
          className="max-w-2xl xl:max-w-3xl"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          style={!isMobile && !prefersReducedMotion ? {
            x: contentX,
            y: contentY
          } : {}}
        >
          {/* Main Headline */}
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-4 sm:mb-5 lg:mb-6"
            variants={headlineVariants}
          >
            <span className="block">Institutional-Grade</span>
            <span className="block gradient-text-shine">Value-Add Real Estate</span>
            <span className="block">in Greater Houston</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p 
            className="text-sm sm:text-base lg:text-lg text-white/70 max-w-xl mb-6 sm:mb-8 lg:mb-10 leading-relaxed"
            variants={itemVariants}
          >
            With 18+ years of Houston market expertise since 2005, we acquire and operate high-potential assets through a disciplined, data-driven approach.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-5"
            variants={itemVariants}
          >
            {/* Primary CTA - Contact-specific style with breathing animation */}
            <Link 
              to="/contact"
              className="group relative"
            >
              <button
                data-testid="hero-schedule-cta"
                className="cta-contact px-6 sm:px-7 py-3 h-12 sm:h-13 rounded-full text-sm w-full sm:w-auto"
              >
                Schedule Your Investment Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Link>
            
            {/* Secondary CTA - Links to Track Record page */}
          
          </motion.div>

          {/* Investor Note */}
          <motion.p 
            className="text-xs sm:text-sm text-white/40 font-medium tracking-wide uppercase"
            variants={itemVariants}
          >
            Available exclusively to verified accredited investors
          </motion.p>
        </motion.div>

        {/* Spacer to push stats bar down - reduced for mobile */}
        <div className="flex-grow min-h-[20px] sm:min-h-[60px] lg:min-h-[80px]" />

        {/* Stats Carousel - Premium Glass Effect */}
        <motion.div 
          className="flex flex-col items-center mb-8 sm:mb-12 lg:mb-16"
          initial="hidden"
          animate="visible"
          variants={statsContainerVariants}
        >
          <motion.div 
            ref={statsContainerRef}
            className="relative rounded-2xl sm:rounded-3xl px-4 sm:px-6 lg:px-8 xl:px-10 py-4 sm:py-5 lg:py-6 w-full max-w-[95%] sm:max-w-2xl lg:max-w-4xl overflow-hidden cursor-pointer"
            style={{
              background: 'rgba(10, 22, 40, 0.6)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
            }}
            onMouseEnter={() => setIsStatsPaused(true)}
            onMouseLeave={() => setIsStatsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            whileHover={!isMobile ? { 
              borderColor: 'rgba(212, 175, 55, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            } : {}}
            transition={{ duration: 0.3 }}
          >
            {/* Inner glow effect */}
            <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none" />
            
            <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-4 sm:gap-6 lg:gap-8 xl:gap-10 relative`}>
              <AnimatePresence mode="popLayout">
                {getVisibleStats().map((stat) => (
                  <motion.div 
                    key={`${currentStatIndex}-${stat.originalIndex}`}
                    className="text-center"
                    variants={statItemVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    <motion.span 
                      className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent block mb-0.5 sm:mb-1"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <AnimatedCounter 
                        value={stat.value} 
                        prefix={stat.prefix || ''} 
                        suffix={stat.suffix} 
                        isRange={stat.isRange}
                        shouldAnimate={hasAnimatedStats}
                      />
                    </motion.span>
                    <span className="text-[10px] sm:text-xs text-white/50 leading-tight block font-medium tracking-wide uppercase">
                      {stat.label}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
          
          {/* Disclaimer */}
          <motion.p 
            className="text-[10px] sm:text-xs text-white/30 mt-3 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
          >
            Past performance is not indicative of future results.
          </motion.p>
        </motion.div>
      </div>

      {/* Background Carousel Progress Indicator - Subtle dots */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-20">
        {heroImages.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`h-1 rounded-full transition-all duration-500 ${
              index === currentImageIndex 
                ? 'w-5 sm:w-6 bg-[#D4AF37]' 
                : 'w-1 bg-white/30 hover:bg-white/50'
            }`}
            whileHover={{ scale: 1.2 }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
